import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Typography, 
  Box, 
  Paper, 
  Grid, 
  Avatar, 
  Divider,
  Chip,
  Button,
  useTheme,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
  FormControl,
  Select,
  MenuItem,
  LinearProgress,
  InputLabel,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  TablePagination,
  Collapse
} from '@mui/material';
import { 
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer 
} from 'recharts';
import { 
  School as SchoolIcon, 
  EmojiEvents as AwardIcon,
  Print as PrintIcon,
  Task as TaskIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassIcon,
  InsertDriveFile as FileIcon,
  OpenInNew as OpenInNewIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  Comment as CommentIcon,
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  FactCheck as FactCheckIcon,
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import api from '../services/api';

const TypedRadarChart = RadarChart as any;
const TypedRadar = Radar as any;
const TypedPolarGrid = PolarGrid as any;
const TypedPolarAngleAxis = PolarAngleAxis as any;
const TypedPolarRadiusAxis = PolarRadiusAxis as any;
const TypedResponsiveContainer = ResponsiveContainer as any;

const Portfolio = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { placementId } = useParams();
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isStudent = user.role === 'STUDENT';
  const isSupervisor = user.role === 'SUPERVISOR';
  const isCoTeacher = user.role === 'CO_TEACHER';
  const hideEvaluatorIdentity = isStudent || isSupervisor;

  useEffect(() => {
    if (isCoTeacher) {
      navigate('/dashboard', { replace: true });
    }
  }, [isCoTeacher, navigate]);

  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [placementData, setPlacementData] = useState<any>(null);
  const [workloadData, setWorkloadData] = useState<any>(null);
  const [evaluations, setEvaluations] = useState<any[]>([]);
  const [itemMapping, setItemMapping] = useState<Record<string, string>>({});

  // Non-student student selector
  const [assignedStudents, setAssignedStudents] = useState<any[]>([]);
  const [selectedPlacementId, setSelectedPlacementId] = useState<string>(placementId || '');

  // Active selected course for the right column (workload items & submissions)
  const [activeCoursePlacementId, setActiveCoursePlacementId] = useState<string>('');
  const [viewWorkloadData, setViewWorkloadData] = useState<any>(null);
  const [viewEvaluations, setViewEvaluations] = useState<any[]>([]);
  const [loadingViewCourse, setLoadingViewCourse] = useState<boolean>(false);
  const [historyOpen, setHistoryOpen] = useState<boolean>(true);

  // Modal file preview
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewFileUrl, setPreviewFileUrl] = useState('');
  const [previewFileName, setPreviewFileName] = useState('');

  const resolveFileUrl = (url?: string) => {
    if (!url) return '';
    if (window.location.protocol === 'https:' && url.startsWith('http://')) {
      url = url.replace(/^http:\/\//i, 'https://');
    }
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    if (url.startsWith('/uploads/')) return `/api/v1${url}`;
    if (!url.startsWith('/')) return `/api/v1/uploads/${url}`;
    return url;
  };

  const isImageFile = (url?: string) => {
    if (!url) return false;
    return /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i.test(url);
  };

  const handleOpenFile = (url: string, name?: string) => {
    const fullUrl = resolveFileUrl(url);
    if (isImageFile(fullUrl)) {
      setPreviewFileUrl(fullUrl);
      setPreviewFileName(name || 'ภาพหลักฐานผลงาน');
      setPreviewModalOpen(true);
    } else {
      window.open(fullUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // Student list view states for non-student roles
  const [studentList, setStudentList] = useState<any[]>([]);
  const [courses, setCourses] = useState<any[]>([]);
  const [searchStudent, setSearchStudent] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('ALL');
  const [selectedYear, setSelectedYear] = useState('ALL');
  const [selectedSemester, setSelectedSemester] = useState('ALL');
  const [selectedMajor, setSelectedMajor] = useState('ALL');
  const [listPage, setListPage] = useState(0);
  const [listRowsPerPage, setListRowsPerPage] = useState(25);
  const [loadingList, setLoadingList] = useState(false);

  const fetchStudentList = async () => {
    try {
      setLoadingList(true);
      const params: any = {};
      if (selectedYear !== 'ALL') params.academic_year = selectedYear;
      if (selectedSemester !== 'ALL') params.semester = selectedSemester;
      if (selectedCourse !== 'ALL') params.course_id = selectedCourse;
      if (selectedMajor !== 'ALL') params.major = selectedMajor;
      if (searchStudent) params.search = searchStudent;

      const res = await api.get('/workload-evaluations/portfolio-students', { params });
      setStudentList(res.data || []);
      setListPage(0);
    } catch (e) {
      console.error('Failed to load portfolio student list:', e);
    } finally {
      setLoadingList(false);
    }
  };

  // On mount for non-students without placementId in URL: load courses, settings, and students
  useEffect(() => {
    if (!isStudent && !placementId) {
      api.get('/courses').then(res => setCourses(res.data || [])).catch(() => {});
      api.get('/settings').then(res => {
        const s = res.data || {};
        if (s.current_academic_year) setSelectedYear(String(s.current_academic_year));
        if (s.current_semester) setSelectedSemester(String(s.current_semester));
      }).catch(() => {});
    }
  }, [isStudent, placementId]);

  useEffect(() => {
    if (!isStudent && !placementId) {
      fetchStudentList();
    }
  }, [isStudent, placementId, selectedYear, selectedSemester, selectedCourse, selectedMajor]);

  // For non-students viewing an individual portfolio: load assigned students for quick switcher
  useEffect(() => {
    if (!isStudent && placementId) {
      api.get('/evaluations/assigned-students')
        .then(res => setAssignedStudents(res.data || []))
        .catch(() => {});
    }
  }, [isStudent, placementId]);

  const availableMajors = React.useMemo(() => {
    const majors = new Set<string>();
    studentList.forEach(s => { if (s.major) majors.add(s.major); });
    return Array.from(majors).sort();
  }, [studentList]);

  const availableYears = React.useMemo(() => {
    const years = new Set<string>();
    if (selectedYear && selectedYear !== 'ALL') years.add(String(selectedYear));
    studentList.forEach(s => { if (s.academic_year) years.add(String(s.academic_year)); });
    ['2569', '2568', '2567', '2566', '2565'].forEach(y => years.add(y));
    return Array.from(years).sort((a, b) => Number(b) - Number(a));
  }, [studentList, selectedYear]);

  const visibleListStudents = listRowsPerPage > 0 
    ? studentList.slice(listPage * listRowsPerPage, listPage * listRowsPerPage + listRowsPerPage) 
    : studentList;

  // Main data fetch for individual student portfolio
  const fetchPortfolioData = async (targetId?: string) => {
    try {
      setLoading(true);
      setErrorMessage('');

      let effectivePlacementId = targetId || placementId;
      let pData: any = null;

      if (effectivePlacementId) {
        const pRes = await api.get(`/placements/${effectivePlacementId}`);
        pData = pRes.data;
      } else if (isStudent) {
        const pRes = await api.get('/placements/my-placement');
        pData = pRes.data;
        effectivePlacementId = pData.id;
      } else if (selectedPlacementId) {
        effectivePlacementId = selectedPlacementId;
        const pRes = await api.get(`/placements/${effectivePlacementId}`);
        pData = pRes.data;
      }

      setPlacementData(pData);

      // Fetch Workload Tasks & Submissions
      if (effectivePlacementId) {
        try {
          const wRes = await api.get(`/workload-evaluations/my-tasks?placement_id=${effectivePlacementId}`);
          setWorkloadData(wRes.data);
          setActiveCoursePlacementId(effectivePlacementId);
          setViewWorkloadData(wRes.data);
        } catch (wErr: any) {
          console.error('Workload tasks error:', wErr);
        }

        // Fetch Competency Evaluations for Radar Chart
        try {
          const eRes = await api.get(`/evaluations/placement/${effectivePlacementId}`);
          setEvaluations(eRes.data || []);
          setViewEvaluations(eRes.data || []);
        } catch (eErr: any) {
          console.error('Competency evaluations error:', eErr);
        }
      } else if (isStudent) {
        // Fallback for student
        const wRes = await api.get('/workload-evaluations/my-tasks');
        setWorkloadData(wRes.data);
        if (wRes.data?.placement_id) {
          setActiveCoursePlacementId(wRes.data.placement_id);
          setViewWorkloadData(wRes.data);
        }
      }
    } catch (err: any) {
      console.error('Failed to fetch portfolio data:', err);
      setErrorMessage(err.response?.data?.message || err.response?.data?.error || 'เกิดข้อผิดพลาดในการโหลดข้อมูลแฟ้มสะสมงาน');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCourse = async (targetId: string) => {
    if (targetId === activeCoursePlacementId) return;
    setActiveCoursePlacementId(targetId);
    if (targetId === (placementData?.id || workloadData?.placement_id)) {
      setViewWorkloadData(workloadData);
      setViewEvaluations(evaluations);
      return;
    }
    try {
      setLoadingViewCourse(true);
      const [wRes, eRes] = await Promise.all([
        api.get(`/workload-evaluations/my-tasks?placement_id=${targetId}`),
        api.get(`/evaluations/placement/${targetId}`).catch(() => ({ data: [] }))
      ]);
      setViewWorkloadData(wRes.data);
      setViewEvaluations(eRes.data || []);
    } catch (err) {
      console.error('Failed to load course details:', err);
    } finally {
      setLoadingViewCourse(false);
    }
  };

  useEffect(() => {
    if (isStudent || placementId) {
      fetchPortfolioData(placementId);
    }
  }, [isStudent, placementId]);

  // Fetch Form Item Mapping for Competency Radar Chart
  useEffect(() => {
    const fetchFormMapping = async () => {
      try {
        const roles = ['PROFESSIONAL', 'SPECIALIST', 'CO_TEACHER'];
        const mapping: Record<string, string> = {};
        for (const role of roles) {
          try {
            const res = await api.get(`/evaluation-forms/active/${role}`);
            const form = res.data;
            if (form && form.sections) {
              form.sections.forEach((section: any) => {
                const match = section.title.match(/([12]\.[1-5])/);
                const code = match ? match[1] : null;
                if (code && section.items) {
                  section.items.forEach((item: any) => {
                    mapping[item.id] = code;
                  });
                }
              });
            }
          } catch (e) {
            // Ignore if active form doesn't exist
          }
        }
        setItemMapping(mapping);
      } catch (err) {
        console.error('Failed to build form mapping', err);
      }
    };
    fetchFormMapping();
  }, []);

  const handlePrint = () => {
    window.print();
  };

  const getSectionCode = (key: string) => {
    if (itemMapping[key]) return itemMapping[key];
    const legacyMatch = key.match(/^([12])_([1-5])_/);
    if (legacyMatch) return `${legacyMatch[1]}.${legacyMatch[2]}`;
    return null;
  };

  const averageRubric = () => {
    if (evaluations.length === 0) return null;
    
    const sectionScores: Record<string, { sum: number; count: number }> = {
      '1.1': { sum: 0, count: 0 },
      '1.2': { sum: 0, count: 0 },
      '1.3': { sum: 0, count: 0 },
      '2.1': { sum: 0, count: 0 },
      '2.2': { sum: 0, count: 0 },
      '2.3': { sum: 0, count: 0 },
      '2.4': { sum: 0, count: 0 },
      '2.5': { sum: 0, count: 0 },
    };

    evaluations.forEach((e: any) => {
      try {
        const c = typeof e.criteria_json === 'string' ? JSON.parse(e.criteria_json) : e.criteria_json;
        if (c) {
          Object.keys(c).forEach(key => {
            const code = getSectionCode(key);
            if (code && sectionScores[code]) {
              sectionScores[code].sum += Number(c[key]) || 0;
              sectionScores[code].count += 1;
            }
          });
        }
      } catch (err) {}
    });

    const getAvg = (code: string) => {
      const sec = sectionScores[code];
      return sec && sec.count > 0 ? sec.sum / sec.count : 0;
    };

    return [
      { subject: '1.1 หน้าที่ครู', A: getAvg('1.1'), fullMark: 4 },
      { subject: '1.2 จัดการเรียนรู้', A: getAvg('1.2'), fullMark: 4 },
      { subject: '1.3 สัมพันธ์ชุมชน', A: getAvg('1.3'), fullMark: 4 },
      { subject: '2.1 ตนเอง', A: getAvg('2.1'), fullMark: 4 },
      { subject: '2.2 วิชาชีพ', A: getAvg('2.2'), fullMark: 4 },
      { subject: '2.3 ผู้รับบริการ', A: getAvg('2.3'), fullMark: 4 },
      { subject: '2.4 เพื่อนร่วมงาน', A: getAvg('2.4'), fullMark: 4 },
      { subject: '2.5 สังคม', A: getAvg('2.5'), fullMark: 4 },
    ];
  };

  const radarData = averageRubric();

  // Resolve display data
  const studentPrefix = placementData?.student_prefix || workloadData?.prefix || '';
  const studentFirst = placementData?.student_first || workloadData?.first_name || '';
  const studentLast = placementData?.student_last || workloadData?.last_name || '';
  const studentFullName = `${studentPrefix}${studentFirst} ${studentLast}`.trim();
  const studentCode = placementData?.student_code || workloadData?.student_code || '-';
  const rawMajor = placementData?.major || workloadData?.major || '-';
  const major = isSupervisor && rawMajor === 'ยังไม่ระบุ' ? '-' : rawMajor;
  const rawSchool = placementData?.school_name || workloadData?.school_name || '-';
  const schoolName = isSupervisor && rawSchool === 'ยังไม่ระบุ' ? '-' : rawSchool;
  const courseCode = placementData?.course_code || workloadData?.course_code || '';
  const courseName = placementData?.course_name || workloadData?.course_name || '';
  const semester = placementData?.semester || workloadData?.semester || '';
  const academicYear = placementData?.academic_year || workloadData?.academic_year || '';

  // Student profile image (1.1)
  const studentRawImage = placementData?.student_image_url || placementData?.image_url || workloadData?.student_image_url || workloadData?.image_url || (isStudent ? user?.image_url : null);
  const studentImageSrc = studentRawImage ? resolveFileUrl(studentRawImage) : undefined;

  // Available placements list (all registered courses - 1.3)
  const availablePlacements = workloadData?.available_placements || [];

  // Current term workload metrics (Summary Card on Left - 1.2)
  const curItems = workloadData?.items || [];
  const curSubmissions = workloadData?.submissions || [];
  const curTotalScore = workloadData?.total_score;
  const curTotalMaxScore = workloadData?.total_max_score || 0;
  const curIsEvaluated = Boolean(workloadData?.is_evaluated || workloadData?.evaluation_status === 'ประเมินแล้ว' || (curTotalScore !== null && curTotalScore !== undefined));
  const curSubmittedIds = new Set(curSubmissions.map((s: any) => s.item_id));
  const curSubmittedCount = curItems.filter((it: any) => curSubmittedIds.has(it.id)).length;

  // Active view course workload metrics (Right Column Rubric Table - 1.3)
  const activeWorkload = viewWorkloadData || workloadData;
  const items = activeWorkload?.items || [];
  const submissions = activeWorkload?.submissions || [];
  const scores = activeWorkload?.scores_json || {};
  const totalScore = activeWorkload?.total_score;
  const totalMaxScore = activeWorkload?.total_max_score || 0;
  const activeCourseCode = activeWorkload?.course_code || courseCode;
  const activeCourseName = activeWorkload?.course_name || courseName;
  const activeFormTitle = activeWorkload?.workload_form_title || 'แบบประเมินภาระงาน';
  const isEvaluated = Boolean(activeWorkload?.is_evaluated || activeWorkload?.evaluation_status === 'ประเมินแล้ว' || (totalScore !== null && totalScore !== undefined));
  const activeTotalScore = totalScore;
  const activeTotalMaxScore = totalMaxScore;
  const isActiveEvaluated = isEvaluated;
  const submittedItemIds = new Set(submissions.map((s: any) => s.item_id));
  const submittedCount = items.filter((it: any) => submittedItemIds.has(it.id)).length;

  // Use evaluations to determine status
  const currentEvals = viewEvaluations.length > 0 ? viewEvaluations : evaluations;
  const profEval = currentEvals.find((e: any) => e.evaluator_role === 'PROFESSIONAL');
  const specEval = currentEvals.find((e: any) => e.evaluator_role === 'SPECIALIST');
  const coEval = currentEvals.find((e: any) => e.evaluator_role === 'CO_TEACHER');

  const isProfEvaluated = Boolean(profEval);
  const isSpecEvaluated = Boolean(specEval);
  const isCoEvaluated = Boolean(coEval);
  const evaluatedCount = (isProfEvaluated ? 1 : 0) + (isSpecEvaluated ? 1 : 0) + (isCoEvaluated ? 1 : 0);

  // Evaluators and Evaluation Status (1.4)
  const getEvaluatorName = (evalObj: any, pPrefix?: string, pFirst?: string, pLast?: string) => {
    if (evalObj && evalObj.evaluator_first_name) {
      return `${evalObj.evaluator_prefix || ''}${evalObj.evaluator_first_name} ${evalObj.evaluator_last_name || ''}`.trim();
    }
    if (pFirst) {
      return `${pPrefix || ''}${pFirst} ${pLast || ''}`.trim();
    }
    return isSupervisor ? '' : 'ยังไม่ระบุ';
  };

  const profSupervisorName = getEvaluatorName(
    profEval, 
    placementData?.prof_supervisor_prefix, 
    placementData?.prof_supervisor_first, 
    placementData?.prof_supervisor_last
  );
  const specSupervisorName = getEvaluatorName(
    specEval, 
    placementData?.spec_supervisor_prefix, 
    placementData?.spec_supervisor_first, 
    placementData?.spec_supervisor_last
  );
  const coTeacherName = getEvaluatorName(
    coEval, 
    placementData?.coteacher_prefix, 
    placementData?.coteacher_first, 
    placementData?.coteacher_last
  );
  const workloadEvaluatorName = activeWorkload?.workload_evaluator_first
    ? `${activeWorkload.workload_evaluator_prefix || ''}${activeWorkload.workload_evaluator_first} ${activeWorkload.workload_evaluator_last || ''}`.trim()
    : '';

  const feedback = activeWorkload?.feedback;
  const evaluatedAt = activeWorkload?.evaluated_at;

  const listTotalCount = studentList.length;
  const listSubmittedCount = studentList.filter(s => Number(s.submitted_items_count) > 0).length;
  const listEvaluatedCount = studentList.filter(s => s.workload_score !== null && s.workload_score !== undefined).length;

  const renderStudentListView = () => {
    return (
      <Box>
        {/* Title Header */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AwardIcon sx={{ color: 'primary.main', fontSize: 32 }} />
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              แฟ้มสะสมผลงานนักศึกษา (Student e-Portfolios)
            </Typography>
          </Box>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
            ตรวจสอบและประเมินแฟ้มสะสมงานดิจิทัล ร่องรอยหลักฐานการปฏิบัติงาน และระดับสมรรถนะนักศึกษา
          </Typography>
        </Box>

        {/* KPI Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Card elevation={1} sx={{ borderRadius: 2, borderLeft: '4px solid #1976d2' }}>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="body2" color="textSecondary">นักศึกษาทั้งหมดในความดูแล</Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mt: 0.5 }}>
                  {listTotalCount} <span style={{ fontSize: '1rem', fontWeight: 400 }}>คน</span>
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Card elevation={1} sx={{ borderRadius: 2, borderLeft: '4px solid #0288d1' }}>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="body2" color="textSecondary">ส่งหลักฐานภาระงานแล้ว</Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'info.main', mt: 0.5 }}>
                  {listSubmittedCount} <span style={{ fontSize: '1rem', fontWeight: 400 }}>คน</span>
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Card elevation={1} sx={{ borderRadius: 2, borderLeft: '4px solid #2e7d32' }}>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="body2" color="textSecondary">ได้รับการประเมินภาระงานแล้ว</Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main', mt: 0.5 }}>
                  {listEvaluatedCount} <span style={{ fontSize: '1rem', fontWeight: 400 }}>คน</span>
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filter and Search Bar */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} sx={{ alignItems: 'center' }}>
            <Grid size={{ xs: 12, sm: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>รายวิชา</InputLabel>
                <Select
                  value={selectedCourse}
                  label="รายวิชา"
                  onChange={(e) => {
                    setSelectedCourse(e.target.value);
                    setListPage(0);
                  }}
                >
                  <MenuItem value="ALL">ทุกรายวิชา (ทั้งหมด)</MenuItem>
                  {courses.map((c: any) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.course_code} {c.course_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>ปีการศึกษา</InputLabel>
                <Select
                  value={selectedYear}
                  label="ปีการศึกษา"
                  onChange={(e) => {
                    setSelectedYear(e.target.value);
                    setListPage(0);
                  }}
                >
                  <MenuItem value="ALL">ทุกปีการศึกษา</MenuItem>
                  {availableYears.map((yr: any) => (
                    <MenuItem key={yr} value={String(yr)}>{yr}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>ภาคเรียน</InputLabel>
                <Select
                  value={selectedSemester}
                  label="ภาคเรียน"
                  onChange={(e) => {
                    setSelectedSemester(e.target.value);
                    setListPage(0);
                  }}
                >
                  <MenuItem value="ALL">ทุกภาคเรียน</MenuItem>
                  <MenuItem value="1">ภาคเรียนที่ 1</MenuItem>
                  <MenuItem value="2">ภาคเรียนที่ 2</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {availableMajors.length > 1 && (
              <Grid size={{ xs: 12, sm: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>สาขาวิชา</InputLabel>
                  <Select
                    value={selectedMajor}
                    label="สาขาวิชา"
                    onChange={(e) => {
                      setSelectedMajor(e.target.value);
                      setListPage(0);
                    }}
                  >
                    <MenuItem value="ALL">ทุกสาขาวิชา</MenuItem>
                    {availableMajors.map((m: any) => (
                      <MenuItem key={m} value={m}>{m}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid size={{ xs: 12, sm: availableMajors.length > 1 ? 3 : 5 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="ค้นหารหัสนักศึกษา, ชื่อ-สกุล, โรงเรียน..."
                value={searchStudent}
                onChange={(e) => setSearchStudent(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') fetchStudentList(); }}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={fetchStudentList}>
                          <SearchIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }
                }}
              />
            </Grid>
          </Grid>
        </Paper>

        {loadingList && <LinearProgress sx={{ mb: 2 }} />}

        {/* Student Table */}
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f0f4f8' }}>
                <TableCell align="center" sx={{ fontWeight: 'bold', width: 50 }}>ที่</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>รหัสนักศึกษา</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 180 }}>ชื่อ - สกุล</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 140 }}>สาขาวิชา</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 160 }}>โรงเรียนฝึกสอน</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 160 }}>รายวิชา</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 110 }}>หลักฐานภาระงาน</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 110 }}>คะแนนภาระงาน</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 120 }}>คะแนนสมรรถนะ</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 110 }}>แฟ้มสะสมงาน</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleListStudents.map((st: any, idx: number) => {
                const displayIndex = (listRowsPerPage > 0 ? listPage * listRowsPerPage : 0) + idx + 1;
                const submitted = Number(st.submitted_items_count) || 0;
                const total = Number(st.total_items_count) || 0;
                const isAllSubmitted = total > 0 && submitted >= total;

                // Competency scores
                const compScores = [st.prof_score, st.spec_score, st.co_score].filter(s => s !== null && s !== undefined);
                const compAvg = compScores.length > 0 
                  ? (compScores.reduce((a: number, b: number) => Number(a) + Number(b), 0) / compScores.length).toFixed(1)
                  : null;

                return (
                  <TableRow 
                    key={st.placement_id} 
                    hover
                    sx={{ 
                      cursor: 'pointer',
                      transition: 'background-color 0.2s',
                      '&:hover': { bgcolor: '#f8fafc' }
                    }}
                    onClick={() => navigate(`/dashboard/portfolio/${st.placement_id}`)}
                  >
                    <TableCell align="center" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                      {displayIndex}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.85rem', color: 'primary.main' }}>
                      {st.student_code}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar 
                          src={st.student_image_url ? resolveFileUrl(st.student_image_url) : undefined} 
                          sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: 'primary.light' }}
                        >
                          {st.first_name ? st.first_name[0] : 'U'}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                            {st.prefix || ''}{st.first_name} {st.last_name}
                          </Typography>
                          {st.group_no && (
                            <Chip label={`หมู่ ${st.group_no}`} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#f8fafc', mt: 0.2 }} />
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                      {st.major}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                      {st.school_name}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                        {st.course_code}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                        {st.course_name}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={`${submitted}/${total || '-'} รายการ`} 
                        size="small" 
                        color={isAllSubmitted ? "success" : (submitted > 0 ? "warning" : "default")}
                        variant={isAllSubmitted ? "filled" : "outlined"}
                        sx={{ height: 22, fontSize: '0.75rem' }} 
                      />
                    </TableCell>
                    <TableCell align="center">
                      {st.workload_score !== null && st.workload_score !== undefined ? (
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.dark' }}>
                            {Number(st.workload_score).toFixed(1)} / {st.workload_max_score || '-'}
                          </Typography>
                          <Chip label="ประเมินแล้ว" size="small" color="success" sx={{ height: 18, fontSize: '0.65rem' }} />
                        </Box>
                      ) : (
                        <Chip label="ยังไม่ประเมิน" size="small" variant="outlined" color="default" sx={{ height: 20, fontSize: '0.7rem' }} />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {compAvg !== null ? (
                        <Typography variant="body2" sx={{ fontWeight: 700, color: Number(compAvg) >= 60 ? 'success.dark' : 'error.main' }}>
                          {compAvg}%
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="textSecondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="contained"
                        size="small"
                        color="primary"
                        startIcon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                        onClick={() => navigate(`/dashboard/portfolio/${st.placement_id}`)}
                        sx={{ minWidth: 100, fontSize: '0.75rem', py: 0.5, borderRadius: 1.5 }}
                      >
                        ดูผลงาน
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}

              {studentList.length === 0 && !loadingList && (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    <Typography variant="body1">ไม่พบข้อมูลนักศึกษาที่ตรงตามเงื่อนไข</Typography>
                    {user.role === 'SUPERVISOR' && (
                      <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                        (สำหรับอาจารย์นิเทศก์ จะแสดงเฉพาะนักศึกษาในสาขาวิชาเดียวกันเท่านั้น)
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Table Pagination */}
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component={Paper}
          count={studentList.length}
          rowsPerPage={listRowsPerPage}
          page={listPage}
          onPageChange={(e, newPage) => setListPage(newPage)}
          onRowsPerPageChange={(e) => {
            setListRowsPerPage(parseInt(e.target.value, 10));
            setListPage(0);
          }}
          labelRowsPerPage="จำนวนแถวต่อหน้า:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} จาก ${count !== -1 ? count : `มากกว่า ${to}`}`}
          sx={{ mt: 1.5, borderRadius: 2 }}
        />
      </Box>
    );
  };

  if (isCoTeacher) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
          ขออภัย ท่านไม่มีสิทธิ์เข้าถึงแฟ้มสะสมผลงานนักศึกษา
        </Alert>
        <Button variant="contained" onClick={() => navigate('/dashboard')}>
          กลับหน้าหลัก
        </Button>
      </Box>
    );
  }

  // If user is not student and no placementId specified in URL, show student list!
  if (!isStudent && !placementId) {
    return renderStudentListView();
  }

  if (loading && !placementData) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <LinearProgress sx={{ mb: 2 }} />
        <Typography variant="body2" color="textSecondary">กำลังโหลดข้อมูลแฟ้มสะสมงาน...</Typography>
      </Box>
    );
  }

  if (errorMessage && !placementData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          {errorMessage}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      '@media print': {
        m: 0,
        p: 0,
        bgcolor: '#ffffff !important',
        '& .no-print': { display: 'none !important' },
        '& .official-print-report': { display: 'block !important' }
      }
    }}>
      <Box className="no-print">
        {/* Back Button for Evaluators */}
        {!isStudent && (
          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/dashboard/portfolio')}
              sx={{ borderRadius: 2 }}
            >
              ย้อนกลับไปยังรายชื่อนักศึกษา (Back to Student List)
            </Button>
          </Box>
        )}

      {/* Header Bar */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' }, 
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        gap: 2,
        mb: 3 
      }} className="no-print">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AwardIcon sx={{ color: 'primary.main', fontSize: 32 }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            แฟ้มสะสมผลงาน (e-Portfolio)
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', width: { xs: '100%', sm: 'auto' }, flexWrap: 'wrap' }}>
          {/* Non-student selector */}
          {!isStudent && assignedStudents.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 260 }}>
              <Select
                value={selectedPlacementId || placementId || ''}
                onChange={(e) => {
                  setSelectedPlacementId(e.target.value);
                  navigate(`/dashboard/portfolio/${e.target.value}`);
                }}
                displayEmpty
              >
                {assignedStudents.map(s => (
                  <MenuItem key={s.placement_id} value={s.placement_id}>
                    {s.student_code} {s.prefix}{s.first_name} {s.last_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}


          <Button 
            variant="outlined" 
            startIcon={<PrintIcon />} 
            onClick={handlePrint}
            size="small"
            fullWidth={isMobile}
            sx={{ borderRadius: 2 }}
          >
            พิมพ์ / บันทึก PDF
          </Button>
          <Chip icon={<AwardIcon />} label="หลักสูตรครุศาสตรบัณฑิต" color="primary" variant="outlined" size="small" />
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Header Profile Banner */}
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ 
            p: { xs: 2.5, md: 3.5 }, 
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`, 
            color: 'white', 
            borderRadius: 3, 
            boxShadow: '0 8px 32px rgba(25, 118, 210, 0.15)' 
          }}>
            <Grid container spacing={3} sx={{ alignItems: 'center' }}>
              <Grid size={{ xs: 12, sm: 'auto' }} sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                <Avatar 
                  src={studentImageSrc}
                  alt={studentFullName}
                  sx={{ 
                    width: { xs: 76, sm: 96 }, 
                    height: { xs: 76, sm: 96 }, 
                    bgcolor: 'white', 
                    color: 'primary.main', 
                    fontSize: { xs: '1.75rem', sm: '2.5rem' }, 
                    fontWeight: 'bold',
                    mx: { xs: 'auto', sm: 0 },
                    boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
                    border: '3px solid rgba(255, 255, 255, 0.9)'
                  }}
                >
                  {studentFirst ? studentFirst[0] : 'U'}
                </Avatar>
              </Grid>
              <Grid size={{ xs: 12, sm: 8 }}>
                <Typography variant="h4" sx={{ textAlign: { xs: 'center', sm: 'left' }, fontSize: { xs: '1.4rem', sm: '1.9rem' }, fontWeight: 700 }}>
                  {studentFullName || 'นักศึกษาฝึกประสบการณ์วิชาชีพครู'}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.95, textAlign: { xs: 'center', sm: 'left' }, fontSize: { xs: '0.95rem', sm: '1.05rem' }, mt: 0.5 }}>
                  รหัสนักศึกษา: <strong>{studentCode}</strong> | สาขาวิชา: <strong>{major}</strong>
                </Typography>
                <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                  <Chip 
                    icon={<SchoolIcon sx={{ color: 'inherit !important' }} />} 
                    label={`สถานศึกษา: ${schoolName}`} 
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 500 }} 
                  />
                  {courseCode && (
                    <Chip 
                      label={`${courseCode} ${courseName}`} 
                      sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 500 }} 
                    />
                  )}
                  {semester && academicYear && (
                    <Chip 
                      label={`ภาคเรียนที่ ${semester}/${academicYear}`} 
                      sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 500 }} 
                    />
                  )}
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Left Column: Summary of Workload & Competency */}
        <Grid size={{ xs: 12, md: 4 }}>
          {/* Workload Summary Card - Current Term (1.2) */}
          <Paper sx={{ 
            p: 3, 
            mb: 3, 
            borderRadius: 3, 
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)', 
            border: '1px solid rgba(0,0,0,0.05)' 
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', color: 'primary.main' }}>
                <TaskIcon sx={{ mr: 1 }} /> สรุปผลภาระงานตามรายวิชา
              </Typography>
              {(semester || academicYear) && (
                <Chip 
                  label={`ภาคปัจจุบัน ${semester}/${academicYear}`} 
                  size="small" 
                  color="primary" 
                  variant="outlined" 
                  sx={{ fontWeight: 600, fontSize: '0.72rem' }} 
                />
              )}
            </Box>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
              รายวิชาปัจจุบัน: <strong>{courseCode} {courseName}</strong>
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2} sx={{ textAlign: 'center', mb: 2 }}>
              <Grid size={{ xs: 6 }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: curSubmittedCount === curItems.length && curItems.length > 0 ? 'success.main' : 'primary.main' }}>
                  {curSubmittedCount} <Typography component="span" variant="body1" color="textSecondary">/ {curItems.length}</Typography>
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>
                  ส่งหลักฐานแล้ว
                </Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                {isStudent ? (
                  <>
                    <Box sx={{ mt: 0.5, mb: 0.5 }}>
                      <Chip
                        label={curIsEvaluated ? "ประเมินแล้ว" : "รอดำเนินการ"}
                        color={curIsEvaluated ? "success" : "warning"}
                        variant={curIsEvaluated ? "filled" : "outlined"}
                        icon={curIsEvaluated ? <CheckCircleIcon sx={{ fontSize: '14px !important' }} /> : <HourglassIcon sx={{ fontSize: '14px !important' }} />}
                        sx={{ fontWeight: 'bold' }}
                      />
                    </Box>
                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>
                      สถานะการประเมินภาระงาน
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: curIsEvaluated ? 'primary.main' : 'text.disabled' }}>
                      {curIsEvaluated ? parseFloat(curTotalScore).toFixed(1) : '-'} 
                      <Typography component="span" variant="body1" color="textSecondary">/ {curTotalMaxScore}</Typography>
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>
                      คะแนนที่ได้รับ
                    </Typography>
                  </>
                )}
              </Grid>
            </Grid>

            {curIsEvaluated ? (
              <Box sx={{ p: 1.5, bgcolor: 'success.50', borderRadius: 2, textAlign: 'center', border: '1px solid', borderColor: 'success.200' }}>
                <Chip 
                  icon={<CheckCircleIcon />}
                  label="ได้รับการประเมินภาระงานแล้ว" 
                  color="success" 
                  size="small" 
                  sx={{ fontWeight: 'bold' }} 
                />
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'success.dark' }}>
                  ส่งงานแล้ว {curSubmittedCount}/{curItems.length} รายการ
                </Typography>
              </Box>
            ) : (
              <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, textAlign: 'center', border: '1px solid #e2e8f0' }}>
                <Chip label="รออาจารย์นิเทศก์ประเมินผล" size="small" variant="outlined" color="warning" />
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                  ส่งงานครบ {curSubmittedCount}/{curItems.length} รายการ
                </Typography>
              </Box>
            )}

            {workloadData?.workload_form_title && (
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 2, textAlign: 'center', fontStyle: 'italic' }}>
                {workloadData.workload_form_title}
              </Typography>
            )}
          </Paper>

          {/* Supervision & Evaluation Status Card (1.4) */}
          <Paper sx={{ 
            p: 3, 
            mb: 3, 
            borderRadius: 3, 
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)', 
            border: '1px solid rgba(0,0,0,0.05)' 
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', color: 'primary.main' }}>
                <FactCheckIcon sx={{ mr: 1, color: 'primary.main' }} /> การประเมินและนิเทศ
              </Typography>
              <Chip 
                label={`ประเมินแล้ว ${evaluatedCount}/3 ฝ่าย`} 
                size="small" 
                color={evaluatedCount === 3 ? "success" : (evaluatedCount > 0 ? "warning" : "default")} 
                sx={{ fontWeight: 600, fontSize: '0.72rem' }} 
              />
            </Box>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1.5 }}>
              สถานะการนิเทศและประเมินสมรรถนะวิชาชีพครูรายบุคคล
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={(evaluatedCount / 3) * 100} 
              color={evaluatedCount === 3 ? "success" : "primary"}
              sx={{ height: 6, borderRadius: 3, mb: 2.5 }}
            />
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {/* 1. อาจารย์นิเทศก์วิชาชีพครู */}
              <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid', borderColor: isProfEvaluated ? '#bbf7d0' : '#e2e8f0' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: !hideEvaluatorIdentity ? 0.5 : 0 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                    1. อาจารย์นิเทศก์วิชาชีพครู:
                  </Typography>
                  <Chip 
                    size="small" 
                    label={isProfEvaluated ? "ประเมินแล้ว" : "รอดำเนินการ"} 
                    color={isProfEvaluated ? "success" : "warning"} 
                    variant={isProfEvaluated ? "filled" : "outlined"}
                    icon={isProfEvaluated ? <CheckCircleIcon sx={{ fontSize: '13px !important' }} /> : <HourglassIcon sx={{ fontSize: '13px !important' }} />}
                    sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                  />
                </Box>
                {!hideEvaluatorIdentity && (
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {profSupervisorName}
                  </Typography>
                )}
                {!isStudent && isProfEvaluated && profEval?.total_score !== undefined && (
                  <Typography variant="caption" sx={{ color: 'success.dark', fontWeight: 600, display: 'block', mt: 0.3 }}>
                    คะแนนประเมิน: {Number(profEval.total_score).toFixed(1)} คะแนน
                  </Typography>
                )}
              </Box>

              {/* 2. อาจารย์นิเทศก์ประจำสาขาวิชาเอก */}
              <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid', borderColor: isSpecEvaluated ? '#bbf7d0' : '#e2e8f0' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: !hideEvaluatorIdentity ? 0.5 : 0 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                    2. อาจารย์นิเทศก์ประจำสาขาวิชาเอก:
                  </Typography>
                  <Chip 
                    size="small" 
                    label={isSpecEvaluated ? "ประเมินแล้ว" : "รอดำเนินการ"} 
                    color={isSpecEvaluated ? "success" : "warning"} 
                    variant={isSpecEvaluated ? "filled" : "outlined"}
                    icon={isSpecEvaluated ? <CheckCircleIcon sx={{ fontSize: '13px !important' }} /> : <HourglassIcon sx={{ fontSize: '13px !important' }} />}
                    sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                  />
                </Box>
                {!hideEvaluatorIdentity && (
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {specSupervisorName}
                  </Typography>
                )}
                {!isStudent && isSpecEvaluated && specEval?.total_score !== undefined && (
                  <Typography variant="caption" sx={{ color: 'success.dark', fontWeight: 600, display: 'block', mt: 0.3 }}>
                    คะแนนประเมิน: {Number(specEval.total_score).toFixed(1)} คะแนน
                  </Typography>
                )}
              </Box>

              {/* 3. ครูพี่เลี้ยงประจำสถานศึกษา */}
              <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid', borderColor: isCoEvaluated ? '#bbf7d0' : '#e2e8f0' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: !hideEvaluatorIdentity ? 0.5 : 0 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                    3. ครูพี่เลี้ยงประจำสถานศึกษา:
                  </Typography>
                  <Chip 
                    size="small" 
                    label={isCoEvaluated ? "ประเมินแล้ว" : "รอดำเนินการ"} 
                    color={isCoEvaluated ? "success" : "warning"} 
                    variant={isCoEvaluated ? "filled" : "outlined"}
                    icon={isCoEvaluated ? <CheckCircleIcon sx={{ fontSize: '13px !important' }} /> : <HourglassIcon sx={{ fontSize: '13px !important' }} />}
                    sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                  />
                </Box>
                {!hideEvaluatorIdentity && (
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {coTeacherName}
                  </Typography>
                )}
                {!isStudent && isCoEvaluated && coEval?.total_score !== undefined && (
                  <Typography variant="caption" sx={{ color: 'success.dark', fontWeight: 600, display: 'block', mt: 0.3 }}>
                    คะแนนประเมิน: {Number(coEval.total_score).toFixed(1)} คะแนน
                  </Typography>
                )}
              </Box>
            </Box>
          </Paper>

          {/* Competency Radar Chart */}
          <Paper sx={{ 
            p: 3, 
            borderRadius: 3, 
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)', 
            border: '1px solid rgba(0,0,0,0.05)' 
          }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
              สมรรถนะวิชาชีพครู
            </Typography>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
              {isStudent ? 'สถานะการประเมินสมรรถนะวิชาชีพครู' : 'คะแนนเฉลี่ยจากการประเมินรอบด้าน'}
            </Typography>
            <Box sx={{ height: 260, width: '100%', mt: 1 }}>
              {isStudent ? (
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 2, textAlign: 'center' }}>
                  <SchoolIcon sx={{ fontSize: 48, color: isProfEvaluated && isSpecEvaluated && isCoEvaluated ? 'success.main' : 'primary.main', mb: 1, opacity: 0.8 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 1.5 }}>
                    การประเมินสมรรถนะตามมาตรฐานวิชาชีพ
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                    <Chip 
                      label={isProfEvaluated ? "วิชาชีพครู: ประเมินแล้ว" : "วิชาชีพครู: รอดำเนินการ"} 
                      size="small" 
                      color={isProfEvaluated ? "success" : "warning"} 
                      variant={isProfEvaluated ? "filled" : "outlined"} 
                    />
                    <Chip 
                      label={isSpecEvaluated ? "วิชาเอก: ประเมินแล้ว" : "วิชาเอก: รอดำเนินการ"} 
                      size="small" 
                      color={isSpecEvaluated ? "success" : "warning"} 
                      variant={isSpecEvaluated ? "filled" : "outlined"} 
                    />
                    <Chip 
                      label={isCoEvaluated ? "ครูพี่เลี้ยง: ประเมินแล้ว" : "ครูพี่เลี้ยง: รอดำเนินการ"} 
                      size="small" 
                      color={isCoEvaluated ? "success" : "warning"} 
                      variant={isCoEvaluated ? "filled" : "outlined"} 
                    />
                  </Box>
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1.5 }}>
                    {isProfEvaluated && isSpecEvaluated && isCoEvaluated 
                      ? 'ได้รับการประเมินสมรรถนะครบทุกด้านเรียบร้อยแล้ว' 
                      : 'อยู่ระหว่างดำเนินการประเมินจากอาจารย์นิเทศก์และครูพี่เลี้ยง'}
                  </Typography>
                </Box>
              ) : radarData && evaluations.length > 0 ? (
                <TypedResponsiveContainer width="100%" height={260} minWidth={0} minHeight={260}>
                  <TypedRadarChart cx="50%" cy="50%" outerRadius="75%" data={radarData}>
                    <TypedPolarGrid />
                    <TypedPolarAngleAxis dataKey="subject" tick={{ fontSize: 9 }} />
                    <TypedPolarRadiusAxis angle={30} domain={[0, 4]} />
                    <TypedRadar
                      name="คะแนนเฉลี่ย"
                      dataKey="A"
                      stroke={theme.palette.primary.main}
                      fill={theme.palette.primary.main}
                      fillOpacity={0.5}
                    />
                  </TypedRadarChart>
                </TypedResponsiveContainer>
              ) : (
                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="textSecondary" variant="body2" sx={{ fontStyle: 'italic' }}>
                    ยังไม่มีข้อมูลการประเมินสมรรถนะ
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Right Column: Workload Rubric Table, Evidence Links, Scores & Supervisor Feedback */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ 
            p: { xs: 2.5, md: 3.5 }, 
            mb: 3, 
            borderRadius: 3, 
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)', 
            border: '1px solid rgba(0,0,0,0.05)' 
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', display: 'flex', alignItems: 'center' }}>
                  <TaskIcon sx={{ mr: 1, color: 'primary.main' }} /> รายการภาระงานตามรายวิชาที่ลงทะเบียน
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                  {activeCourseCode} {activeCourseName} | {activeFormTitle} (คะแนนเต็ม {activeTotalMaxScore} คะแนน)
                </Typography>
              </Box>

              {isActiveEvaluated && (
                <Chip 
                  label={isStudent ? "ได้รับการประเมินแล้ว" : `ประเมินแล้ว: ${parseFloat(activeTotalScore).toFixed(1)} / ${activeTotalMaxScore} คะแนน`} 
                  color="success" 
                  sx={{ fontWeight: 'bold', fontSize: '0.85rem' }} 
                />
              )}
            </Box>
            <Divider sx={{ mb: 2.5 }} />

            {/* Course Selector Tabs (If student has multiple courses registered) (1.3) */}
            {availablePlacements.length > 1 && (
              <Box sx={{ mb: 2.5, p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
                    <HistoryIcon sx={{ fontSize: 18, mr: 0.5, color: 'primary.main' }} /> เลือกรอบรายวิชาเพื่อดูภาระงานและหลักฐาน:
                  </Typography>
                  <Button 
                    size="small" 
                    variant="text" 
                    onClick={() => setHistoryOpen(!historyOpen)}
                    endIcon={historyOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    sx={{ fontSize: '0.75rem', py: 0 }}
                  >
                    {historyOpen ? 'ซ่อนตารางประวัติผลการเรียน' : 'แสดงตารางประวัติผลการเรียนทุกวิชา'}
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {availablePlacements.map((p: any) => {
                    const isSelected = p.placement_id === activeCoursePlacementId;
                    const isCurrent = Boolean(p.is_current_term);
                    const isEval = Boolean(p.is_evaluated || p.evaluation_status === 'ประเมินแล้ว' || (p.workload_score !== null && p.workload_score !== undefined));
                    return (
                      <Button
                        key={p.placement_id}
                        variant={isSelected ? 'contained' : 'outlined'}
                        color={isSelected ? 'primary' : 'inherit'}
                        size="small"
                        onClick={() => handleSelectCourse(p.placement_id)}
                        sx={{ 
                          borderRadius: 2, 
                          textTransform: 'none',
                          fontWeight: isSelected ? 700 : 500,
                          bgcolor: isSelected ? 'primary.main' : 'white',
                          borderColor: isSelected ? 'primary.main' : '#cbd5e1',
                          boxShadow: isSelected ? 1 : 'none'
                        }}
                      >
                        {p.course_code} {p.course_name} ({p.semester}/{p.academic_year})
                        {isCurrent && (
                          <Chip label="ภาคปัจจุบัน" size="small" color={isSelected ? 'secondary' : 'primary'} sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                        )}
                        {isEval && !isCurrent && (
                          <Chip label="ประเมินแล้ว" size="small" color="success" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                        )}
                      </Button>
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* Historical Registered Courses Summary Table (1.3) */}
            {availablePlacements.length > 0 && (
              <Collapse in={historyOpen}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'text.secondary' }}>
                    ตารางแสดงผลการเรียนและภาระงานทุกรายวิชาที่ลงทะเบียน (Teaching Practice Courses History):
                  </Typography>
                  <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: 'white' }}>
                    <Table size="small">
                      <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', width: '5%', textAlign: 'center' }}>ที่</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '32%' }}>รายวิชาฝึกประสบการณ์</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '12%', textAlign: 'center' }}>ภาค/ปีการศึกษา</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '18%' }}>สถานศึกษา</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '15%', textAlign: 'center' }}>{isStudent ? 'สถานะภาระงาน' : 'คะแนนภาระงาน'}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '10%', textAlign: 'center' }}>การประเมิน</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '8%', textAlign: 'center' }}>ภาระงาน</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {availablePlacements.map((p: any, idx: number) => {
                          const isSelected = p.placement_id === activeCoursePlacementId;
                          const isEval = Boolean(p.is_evaluated || p.evaluation_status === 'ประเมินแล้ว' || (p.workload_score !== null && p.workload_score !== undefined));
                          const maxScore = p.workload_max_score || 0;

                          return (
                            <TableRow 
                              key={p.placement_id}
                              hover
                              sx={{ 
                                bgcolor: isSelected ? 'rgba(25, 118, 210, 0.06)' : (idx % 2 === 0 ? 'white' : '#fcfcfd'),
                                borderLeft: isSelected ? '4px solid #1976d2' : 'none'
                              }}
                            >
                              <TableCell align="center" sx={{ fontWeight: 600, color: 'text.secondary' }}>{idx + 1}</TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: isSelected ? 'primary.main' : 'text.primary' }}>
                                  {p.course_code} {p.course_name}
                                </Typography>
                                {p.is_current_term && (
                                  <Chip label="ภาคเรียนปัจจุบัน" size="small" color="primary" sx={{ height: 18, fontSize: '0.65rem', mt: 0.3 }} />
                                )}
                              </TableCell>
                              <TableCell align="center">
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {p.semester}/{p.academic_year}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                  {p.school_name || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                {isStudent ? (
                                  <Chip 
                                    label={isEval ? "ประเมินแล้ว" : "รอประเมิน"} 
                                    size="small" 
                                    color={isEval ? "success" : "warning"}
                                    variant={isEval ? "filled" : "outlined"}
                                    sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600 }} 
                                  />
                                ) : isEval ? (
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.dark' }}>
                                      {Number(p.workload_score).toFixed(1)} / {maxScore}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Chip label="รอประเมิน" size="small" variant="outlined" color="warning" sx={{ height: 20, fontSize: '0.68rem' }} />
                                )}
                              </TableCell>
                              <TableCell align="center">
                                {isEval ? (
                                  <Chip 
                                    label="ประเมินแล้ว" 
                                    size="small" 
                                    color="success" 
                                    sx={{ height: 22, fontSize: '0.72rem', fontWeight: 600 }} 
                                  />
                                ) : (
                                  <Typography variant="caption" color="textSecondary">-</Typography>
                                )}
                              </TableCell>
                              <TableCell align="center">
                                <Button
                                  variant={isSelected ? "contained" : "outlined"}
                                  size="small"
                                  color={isSelected ? "primary" : "inherit"}
                                  onClick={() => handleSelectCourse(p.placement_id)}
                                  sx={{ minWidth: 64, fontSize: '0.7rem', py: 0.3, borderRadius: 1.5 }}
                                >
                                  {isSelected ? 'กำลังดู' : 'ดูภาระงาน'}
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Collapse>
            )}

            {loadingViewCourse && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

            {/* If course has no form linked */}
            {!activeWorkload?.workload_form_id && !loadingViewCourse && (
              <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
                รายวิชานี้ยังไม่ได้ผูกแบบประเมินภาระงาน กรุณาติดต่อผู้รับผิดชอบหลักสูตรเพื่อดำเนินการ
              </Alert>
            )}

            {/* Rubric Items Table */}
            <TableContainer sx={{ border: '1px solid #f0f0f0', borderRadius: 2, overflow: 'hidden' }}>
              <Table size="small">
                <TableHead sx={{ bgcolor: 'grey.100' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', width: '6%', textAlign: 'center' }}>ลำดับ</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '38%' }}>รายการภาระงานและเกณฑ์การประเมิน</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '12%', textAlign: 'center' }}>คะแนนเต็ม</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '12%', textAlign: 'center' }}>{isStudent ? 'สถานะการประเมิน' : 'คะแนนที่ได้'}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '32%' }}>หลักฐานที่ส่ง / ลิงก์ผลงาน</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item: any, idx: number) => {
                    const itemSubs = submissions.filter((s: any) => s.item_id === item.id);
                    const itemScore = scores[item.id];
                    const hasScore = itemScore !== undefined && itemScore !== null && itemScore !== '';
                    const hasSub = itemSubs.length > 0;

                    return (
                      <TableRow 
                        key={item.id} 
                        hover
                        sx={{ 
                          bgcolor: idx % 2 === 0 ? 'white' : '#fcfcfd',
                          '&:last-child td, &:last-child th': { border: 0 }
                        }}
                      >
                        <TableCell align="center" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                          {idx + 1}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                            {item.title}
                          </Typography>
                          {item.description && (
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                              {item.description}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {Number(item.max_score) > 0 ? (
                            <Chip 
                              label={`${item.max_score} คะแนน`} 
                              size="small" 
                              variant="outlined" 
                              sx={{ fontWeight: 600, fontSize: '0.75rem' }} 
                            />
                          ) : (
                            <Chip 
                              label="ไม่คิดคะแนน" 
                              size="small" 
                              color="info"
                              variant="outlined" 
                              sx={{ fontWeight: 600, fontSize: '0.75rem' }} 
                            />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {Number(item.max_score) === 0 ? (
                            <Typography variant="caption" color="textSecondary">ส่งหลักฐาน</Typography>
                          ) : isStudent ? (
                            hasScore ? (
                              <Chip 
                                label="ประเมินแล้ว" 
                                size="small" 
                                color="success" 
                                sx={{ fontWeight: 'bold', minWidth: 44 }} 
                              />
                            ) : (
                              <Chip 
                                label="รอประเมิน" 
                                size="small" 
                                variant="outlined" 
                                color="warning" 
                                sx={{ fontSize: '0.72rem' }} 
                              />
                            )
                          ) : hasScore ? (
                            <Chip 
                              label={`${parseFloat(itemScore).toFixed(1)}`} 
                              size="small" 
                              color="primary" 
                              sx={{ fontWeight: 'bold', minWidth: 44 }} 
                            />
                          ) : (
                            <Typography variant="caption" color="textSecondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {hasSub ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              {itemSubs.map((sub: any) => (
                                <Box key={sub.id} sx={{ p: 1, bgcolor: '#f8fafc', borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                                    {sub.file_url && (
                                      <Button
                                        size="small"
                                        variant="contained"
                                        color={isImageFile(sub.file_url) ? 'secondary' : 'primary'}
                                        startIcon={isImageFile(sub.file_url) ? <VisibilityIcon sx={{ fontSize: 16 }} /> : <FileIcon sx={{ fontSize: 16 }} />}
                                        onClick={() => handleOpenFile(sub.file_url, sub.file_name)}
                                        sx={{ textTransform: 'none', py: 0.2, px: 1, fontSize: '0.75rem', borderRadius: 1 }}
                                      >
                                        {isImageFile(sub.file_url) ? 'ดูรูปภาพ' : 'เปิดไฟล์หลักฐาน'}
                                      </Button>
                                    )}

                                    {sub.file_url_external && (
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        color="info"
                                        startIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                                        href={sub.file_url_external}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{ textTransform: 'none', py: 0.2, px: 1, fontSize: '0.75rem', borderRadius: 1 }}
                                      >
                                        เปิดลิงก์ผลงาน
                                      </Button>
                                    )}

                                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                                      {new Date(sub.created_at).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: '2-digit' })}
                                    </Typography>
                                  </Box>

                                  {sub.notes && (
                                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', fontStyle: 'italic', bgcolor: '#fff', p: 0.5, borderRadius: 1 }}>
                                      บันทึก: {sub.notes}
                                    </Typography>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          ) : (
                            <Chip 
                              label="ยังไม่ส่งงาน" 
                              size="small" 
                              color="default" 
                              variant="outlined" 
                              sx={{ fontSize: '0.75rem', color: 'text.disabled' }} 
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {items.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                        <Typography color="textSecondary" variant="body2">
                          ยังไม่มีรายการภาระงานที่กำหนดในวิชานี้
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Total Row */}
            {items.length > 0 && (
              <Box sx={{ 
                mt: 2, 
                p: 2, 
                bgcolor: '#f8fafc', 
                borderRadius: 2, 
                border: '1px solid #e2e8f0', 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                flexWrap: 'wrap',
                gap: 1
              }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    {isStudent ? 'สรุปการส่งงานและสถานะการประเมิน:' : 'สรุปการส่งงานและการให้คะแนน:'}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    ส่งงานแล้ว {submittedCount} จาก {items.length} รายการ ({items.length > 0 ? ((submittedCount / items.length) * 100).toFixed(0) : 0}%)
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  {isStudent ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                      <Chip
                        label={isActiveEvaluated ? "ได้รับการประเมินภาระงานแล้ว" : "รอดำเนินการประเมิน"}
                        color={isActiveEvaluated ? "success" : "warning"}
                        variant={isActiveEvaluated ? "filled" : "outlined"}
                        icon={isActiveEvaluated ? <CheckCircleIcon sx={{ fontSize: '14px !important' }} /> : <HourglassIcon sx={{ fontSize: '14px !important' }} />}
                        sx={{ fontWeight: 'bold' }}
                      />
                    </Box>
                  ) : (
                    <>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: isActiveEvaluated ? 'primary.main' : 'text.disabled' }}>
                        {isActiveEvaluated ? parseFloat(activeTotalScore).toFixed(1) : '-'} / {activeTotalMaxScore} <Typography component="span" variant="body2" color="textSecondary">คะแนน</Typography>
                      </Typography>
                      {isActiveEvaluated && (
                        <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'success.dark' }}>
                          (ได้รับการประเมินภาระงานแล้ว)
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              </Box>
            )}
          </Paper>

          {/* Supervisor & Evaluator Feedback & Comments Section: HIDDEN for STUDENT (1.5) */}
          {!isStudent && (
            <Paper sx={{ 
              p: { xs: 2.5, md: 3.5 }, 
              borderRadius: 3, 
              boxShadow: '0 4px 20px rgba(0,0,0,0.05)', 
              border: '1px solid rgba(0,0,0,0.05)',
              mt: 3
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', color: '#b45309' }}>
                  <CommentIcon sx={{ mr: 1, color: '#f59e0b' }} /> ข้อเสนอแนะและข้อคิดเห็นจากผู้ประเมิน
                </Typography>
                <Chip label="เฉพาะอาจารย์และเจ้าหน้าที่" size="small" variant="outlined" color="warning" sx={{ fontSize: '0.7rem' }} />
              </Box>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
                ข้อคิดเห็นและข้อเสนอแนะเพื่อการพัฒนาจากผู้ประเมินทั้ง 3 ฝ่าย และการประเมินภาระงาน
              </Typography>
              <Divider sx={{ mb: 2.5 }} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* 1. ข้อคิดเห็นจากอาจารย์นิเทศก์วิชาชีพครู */}
                <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2, borderLeft: '4px solid #1976d2', border: '1px solid #e2e8f0', borderLeftWidth: 4 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      อาจารย์นิเทศก์วิชาชีพครู {!hideEvaluatorIdentity && profSupervisorName && profSupervisorName !== 'ยังไม่ระบุ' ? `(${profSupervisorName})` : ''}
                    </Typography>
                    {profEval?.created_at && (
                      <Typography variant="caption" color="textSecondary">
                        {new Date(profEval.created_at).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: '2-digit' })}
                      </Typography>
                    )}
                  </Box>
                  {profEval?.feedback && profEval.feedback.trim() ? (
                    <Typography variant="body2" sx={{ color: 'text.primary', whiteSpace: 'pre-wrap', lineHeight: 1.6, mt: 0.5 }}>
                      "{profEval.feedback}"
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic', display: 'block', mt: 0.5 }}>
                      {isProfEvaluated ? 'ไม่มีข้อเสนอแนะเพิ่มเติม' : 'อยู่ระหว่างรอการประเมิน'}
                    </Typography>
                  )}
                </Box>

                {/* 2. ข้อคิดเห็นจากอาจารย์นิเทศก์ประจำสาขาวิชาเอก */}
                <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2, borderLeft: '4px solid #9c27b0', border: '1px solid #e2e8f0', borderLeftWidth: 4 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#7b1fa2' }}>
                      อาจารย์นิเทศก์ประจำสาขาวิชาเอก {!hideEvaluatorIdentity && specSupervisorName && specSupervisorName !== 'ยังไม่ระบุ' ? `(${specSupervisorName})` : ''}
                    </Typography>
                    {specEval?.created_at && (
                      <Typography variant="caption" color="textSecondary">
                        {new Date(specEval.created_at).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: '2-digit' })}
                      </Typography>
                    )}
                  </Box>
                  {specEval?.feedback && specEval.feedback.trim() ? (
                    <Typography variant="body2" sx={{ color: 'text.primary', whiteSpace: 'pre-wrap', lineHeight: 1.6, mt: 0.5 }}>
                      "{specEval.feedback}"
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic', display: 'block', mt: 0.5 }}>
                      {isSpecEvaluated ? 'ไม่มีข้อเสนอแนะเพิ่มเติม' : 'อยู่ระหว่างรอการประเมิน'}
                    </Typography>
                  )}
                </Box>

                {/* 3. ข้อคิดเห็นจากครูพี่เลี้ยงประจำสถานศึกษา */}
                <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2, borderLeft: '4px solid #2e7d32', border: '1px solid #e2e8f0', borderLeftWidth: 4 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'success.main' }}>
                      ครูพี่เลี้ยงประจำสถานศึกษา {!hideEvaluatorIdentity && coTeacherName && coTeacherName !== 'ยังไม่ระบุ' ? `(${coTeacherName})` : ''}
                    </Typography>
                    {coEval?.created_at && (
                      <Typography variant="caption" color="textSecondary">
                        {new Date(coEval.created_at).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: '2-digit' })}
                      </Typography>
                    )}
                  </Box>
                  {coEval?.feedback && coEval.feedback.trim() ? (
                    <Typography variant="body2" sx={{ color: 'text.primary', whiteSpace: 'pre-wrap', lineHeight: 1.6, mt: 0.5 }}>
                      "{coEval.feedback}"
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic', display: 'block', mt: 0.5 }}>
                      {isCoEvaluated ? 'ไม่มีข้อเสนอแนะเพิ่มเติม' : 'อยู่ระหว่างรอการประเมิน'}
                    </Typography>
                  )}
                </Box>

                {/* 4. ข้อคิดเห็นจากการประเมินภาระงาน */}
                {feedback && feedback.trim() && (
                  <Box sx={{ p: 2, bgcolor: '#fffbeb', borderRadius: 2, borderLeft: '4px solid #f59e0b', border: '1px solid #fef3c7', borderLeftWidth: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#b45309' }}>
                        การกรอกคะแนนภาระงาน (Workload Evaluation) {!hideEvaluatorIdentity && workloadEvaluatorName ? `(${workloadEvaluatorName})` : ''}
                      </Typography>
                      {evaluatedAt && (
                        <Typography variant="caption" color="textSecondary">
                          {new Date(evaluatedAt).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: '2-digit' })}
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="body2" sx={{ color: '#78350f', whiteSpace: 'pre-wrap', lineHeight: 1.6, mt: 0.5 }}>
                      "{feedback}"
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          )}
        </Grid>
      </Grid>
      </Box> {/* End of .no-print */}

      {/* Official Registry Print Report (Visible only when printed / PDF export) */}
      <Box 
        className="official-print-report" 
        sx={{ 
          display: 'none',
          '@media print': {
            display: 'block !important',
            color: '#000000 !important',
            bgcolor: '#ffffff !important',
            fontFamily: "'Sarabun', 'TH Sarabun New', Tahoma, sans-serif !important",
            fontSize: '11pt',
            lineHeight: 1.4,
            width: '100%',
            maxWidth: '100%',
            p: '2mm'
          }
        }}
      >
        {/* Document Header */}
        <Box sx={{ textAlign: 'center', mb: 2, pb: 1, borderBottom: '2px solid #000' }}>
          <Typography sx={{ fontSize: '16pt', fontWeight: 800, color: '#000', mb: 0.5 }}>
            คณะครุศาสตร์ มหาวิทยาลัยราชภัฏกำแพงเพชร
          </Typography>
          <Typography sx={{ fontSize: '13pt', fontWeight: 700, color: '#000', mb: 0.2 }}>
            แบบรายงานผลการประเมินและการฝึกประสบการณ์วิชาชีพครู (e-Portfolio & Practicum Assessment)
          </Typography>
          <Typography sx={{ fontSize: '10pt', color: '#333' }}>
            เอกสารหลักฐานสำหรับส่งฝ่ายทะเบียนและประมวลผลการศึกษา คณะครุศาสตร์
          </Typography>
        </Box>

        {/* Student Profile & Photo Box */}
        <Box sx={{ 
          display: 'flex', 
          border: '1px solid #000', 
          p: 1.5, 
          mb: 2, 
          alignItems: 'flex-start',
          bgcolor: '#fff',
          pageBreakInside: 'avoid'
        }}>
          {/* Photo */}
          <Box sx={{ 
            width: '105px', 
            height: '135px', 
            border: '1px solid #000', 
            mr: 2, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            overflow: 'hidden', 
            flexShrink: 0,
            bgcolor: '#f5f5f5' 
          }}>
            {studentImageSrc ? (
              <img 
                src={studentImageSrc} 
                alt={studentFullName} 
                style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
              />
            ) : (
              <Box sx={{ textAlign: 'center', p: 1 }}>
                <Typography sx={{ fontSize: '9pt', color: '#666', fontWeight: 600 }}>
                  รูปถ่ายนักศึกษา<br />1.5 - 2 นิ้ว
                </Typography>
              </Box>
            )}
          </Box>

          {/* Student Info Details */}
          <Box sx={{ flexGrow: 1 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10.5pt' }}>
              <tbody>
                <tr>
                  <td style={{ width: '18%', padding: '2px 4px', fontWeight: 'bold' }}>ชื่อ - นามสกุล:</td>
                  <td style={{ width: '42%', padding: '2px 4px' }}>{studentFullName}</td>
                  <td style={{ width: '18%', padding: '2px 4px', fontWeight: 'bold' }}>รหัสนักศึกษา:</td>
                  <td style={{ width: '22%', padding: '2px 4px', fontFamily: 'monospace', fontWeight: 'bold' }}>{studentCode}</td>
                </tr>
                <tr>
                  <td style={{ padding: '2px 4px', fontWeight: 'bold' }}>สาขาวิชา:</td>
                  <td style={{ padding: '2px 4px' }}>{major}</td>
                  <td style={{ padding: '2px 4px', fontWeight: 'bold' }}>หลักสูตร:</td>
                  <td style={{ padding: '2px 4px' }}>ครุศาสตรบัณฑิต (ค.บ.)</td>
                </tr>
                <tr>
                  <td style={{ padding: '2px 4px', fontWeight: 'bold' }}>สถานศึกษา:</td>
                  <td style={{ padding: '2px 4px' }} colSpan={3}>{schoolName}</td>
                </tr>
                <tr>
                  <td style={{ padding: '2px 4px', fontWeight: 'bold' }}>รายวิชาฝึกสอน:</td>
                  <td style={{ padding: '2px 4px' }}>{activeCourseCode} {activeCourseName}</td>
                  <td style={{ padding: '2px 4px', fontWeight: 'bold' }}>ภาค/ปีการศึกษา:</td>
                  <td style={{ padding: '2px 4px' }}>ภาคเรียนที่ {semester || '-'} / {academicYear || '-'}</td>
                </tr>
                <tr>
                  <td style={{ padding: '2px 4px', fontWeight: 'bold' }}>อาจารย์นิเทศก์:</td>
                  <td style={{ padding: '2px 4px' }} colSpan={3}>
                    {profSupervisorName ? `${profSupervisorName} (วิชาชีพ)` : '-'}
                    {specSupervisorName ? ` / ${specSupervisorName} (วิชาเอก)` : ''}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '2px 4px', fontWeight: 'bold' }}>ครูพี่เลี้ยง:</td>
                  <td style={{ padding: '2px 4px' }} colSpan={3}>{coTeacherName || '-'}</td>
                </tr>
              </tbody>
            </table>
          </Box>
        </Box>

        {/* Section 1: Competency Evaluation Table */}
        <Box sx={{ mb: 2, pageBreakInside: 'avoid' }}>
          <Typography sx={{ fontSize: '11pt', fontWeight: 800, mb: 0.5 }}>
            1. ผลการประเมินสมรรถนะวิชาชีพครู (Teaching Competency Evaluation)
          </Typography>
          <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000', fontSize: '10pt', textAlign: 'left' }}>
            <thead>
              <tr style={{ backgroundColor: '#f0f0f0', borderBottom: '1px solid #000' }}>
                <th style={{ border: '1px solid #000', padding: '4px 6px', width: '8%', textAlign: 'center' }}>ลำดับ</th>
                <th style={{ border: '1px solid #000', padding: '4px 6px', width: '37%' }}>ด้านการประเมิน</th>
                <th style={{ border: '1px solid #000', padding: '4px 6px', width: '30%' }}>ผู้ประเมิน</th>
                <th style={{ border: '1px solid #000', padding: '4px 6px', width: '13%', textAlign: 'center' }}>สถานะ</th>
                <th style={{ border: '1px solid #000', padding: '4px 6px', width: '12%', textAlign: 'center' }}>คะแนน</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ border: '1px solid #000', padding: '4px 6px', textAlign: 'center' }}>1</td>
                <td style={{ border: '1px solid #000', padding: '4px 6px' }}>อาจารย์นิเทศก์ด้านการสอน (วิชาชีพครู)</td>
                <td style={{ border: '1px solid #000', padding: '4px 6px' }}>{profSupervisorName || '-'}</td>
                <td style={{ border: '1px solid #000', padding: '4px 6px', textAlign: 'center' }}>
                  {isProfEvaluated ? 'ประเมินแล้ว' : 'รอดำเนินการ'}
                </td>
                <td style={{ border: '1px solid #000', padding: '4px 6px', textAlign: 'center', fontWeight: 'bold' }}>
                  {profEval?.total_score != null ? Number(profEval.total_score).toFixed(1) : '-'}
                </td>
              </tr>
              <tr>
                <td style={{ border: '1px solid #000', padding: '4px 6px', textAlign: 'center' }}>2</td>
                <td style={{ border: '1px solid #000', padding: '4px 6px' }}>อาจารย์นิเทศก์ด้านวิชาเอก</td>
                <td style={{ border: '1px solid #000', padding: '4px 6px' }}>{specSupervisorName || '-'}</td>
                <td style={{ border: '1px solid #000', padding: '4px 6px', textAlign: 'center' }}>
                  {isSpecEvaluated ? 'ประเมินแล้ว' : 'รอดำเนินการ'}
                </td>
                <td style={{ border: '1px solid #000', padding: '4px 6px', textAlign: 'center', fontWeight: 'bold' }}>
                  {specEval?.total_score != null ? Number(specEval.total_score).toFixed(1) : '-'}
                </td>
              </tr>
              <tr>
                <td style={{ border: '1px solid #000', padding: '4px 6px', textAlign: 'center' }}>3</td>
                <td style={{ border: '1px solid #000', padding: '4px 6px' }}>ครูพี่เลี้ยงประจำสถานศึกษา</td>
                <td style={{ border: '1px solid #000', padding: '4px 6px' }}>{coTeacherName || '-'}</td>
                <td style={{ border: '1px solid #000', padding: '4px 6px', textAlign: 'center' }}>
                  {isCoEvaluated ? 'ประเมินแล้ว' : 'รอดำเนินการ'}
                </td>
                <td style={{ border: '1px solid #000', padding: '4px 6px', textAlign: 'center', fontWeight: 'bold' }}>
                  {coEval?.total_score != null ? Number(coEval.total_score).toFixed(1) : '-'}
                </td>
              </tr>
            </tbody>
          </table>
        </Box>

        {/* Section 2: Workload & Rubric Evaluation Table */}
        <Box sx={{ mb: 2, pageBreakInside: 'avoid' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
            <Typography sx={{ fontSize: '11pt', fontWeight: 800 }}>
              2. ผลการประเมินภาระงานตามรายวิชา (Workload Rubric Evaluation)
            </Typography>
            <Typography sx={{ fontSize: '9.5pt', color: '#333' }}>
              แบบประเมิน: <strong>{activeFormTitle}</strong>
            </Typography>
          </Box>
          <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000', fontSize: '9.5pt', textAlign: 'left' }}>
            <thead>
              <tr style={{ backgroundColor: '#f0f0f0', borderBottom: '1px solid #000' }}>
                <th style={{ border: '1px solid #000', padding: '4px 6px', width: '6%', textAlign: 'center' }}>ที่</th>
                <th style={{ border: '1px solid #000', padding: '4px 6px', width: '58%' }}>รายการภาระงาน / งานที่มอบหมายตามเกณฑ์</th>
                <th style={{ border: '1px solid #000', padding: '4px 6px', width: '12%', textAlign: 'center' }}>คะแนนเต็ม</th>
                <th style={{ border: '1px solid #000', padding: '4px 6px', width: '12%', textAlign: 'center' }}>การส่งงาน</th>
                <th style={{ border: '1px solid #000', padding: '4px 6px', width: '12%', textAlign: 'center' }}>คะแนนที่ได้</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ border: '1px solid #000', padding: '8px', textAlign: 'center', color: '#666' }}>
                    ไม่มีรายการภาระงานสำหรับรายวิชานี้
                  </td>
                </tr>
              ) : (
                items.map((item: any, idx: number) => {
                  const hasSubmitted = submittedItemIds.has(item.id);
                  const itemScore = scores[item.id];
                  return (
                    <tr key={item.id || idx}>
                      <td style={{ border: '1px solid #000', padding: '3px 6px', textAlign: 'center' }}>{idx + 1}</td>
                      <td style={{ border: '1px solid #000', padding: '3px 6px' }}>{item.title || item.description}</td>
                      <td style={{ border: '1px solid #000', padding: '3px 6px', textAlign: 'center' }}>{item.max_score}</td>
                      <td style={{ border: '1px solid #000', padding: '3px 6px', textAlign: 'center' }}>
                        {hasSubmitted ? 'ส่งแล้ว' : 'ยังไม่ส่ง'}
                      </td>
                      <td style={{ border: '1px solid #000', padding: '3px 6px', textAlign: 'center', fontWeight: 'bold' }}>
                        {itemScore !== undefined && itemScore !== null ? itemScore : (isActiveEvaluated ? 0 : '-')}
                      </td>
                    </tr>
                  );
                })
              )}
              {/* Summary Row */}
              <tr style={{ backgroundColor: '#f9f9f9', fontWeight: 'bold', borderTop: '2px solid #000' }}>
                <td colSpan={2} style={{ border: '1px solid #000', padding: '5px 8px', textAlign: 'right' }}>
                  รวมคะแนนภาระงานทั้งสิ้น:
                </td>
                <td style={{ border: '1px solid #000', padding: '5px 6px', textAlign: 'center' }}>
                  {activeTotalMaxScore} คะแนน
                </td>
                <td style={{ border: '1px solid #000', padding: '5px 6px', textAlign: 'center' }}>
                  ส่ง {submittedCount} / {items.length} ข้อ
                </td>
                <td style={{ border: '1px solid #000', padding: '5px 6px', textAlign: 'center', fontSize: '10.5pt' }}>
                  {isActiveEvaluated ? (activeTotalScore != null ? parseFloat(activeTotalScore).toFixed(1) : '0') : '-'} คะแนน
                </td>
              </tr>
            </tbody>
          </table>
          {feedback && (
            <Box sx={{ mt: 1, p: 1, border: '1px dashed #000', bgcolor: '#fff', fontSize: '9.5pt' }}>
              <strong>ข้อเสนอแนะเพิ่มเติมจากอาจารย์นิเทศก์:</strong> {feedback}
            </Box>
          )}
        </Box>

        {/* Section 3: Official Registry Certification & Signature Box */}
        <Box sx={{ 
          mt: 3, 
          pt: 1.5, 
          pageBreakInside: 'avoid',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end'
        }}>
          <Box sx={{ width: '100%', mb: 1, textAlign: 'justify', fontSize: '10pt' }}>
            ขอรับรองว่า ข้อมูลผลการประเมินสมรรถนะวิชาชีพครูและแฟ้มสะสมงาน (e-Portfolio) ของนักศึกษาดังกล่าวข้างต้น ได้รับการประเมินและตรวจสอบความถูกต้องครบถ้วนตามระเบียบและเกณฑ์มาตรฐานของคณะครุศาสตร์ มหาวิทยาลัยราชภัฏกำแพงเพชร เรียบร้อยแล้วทุกประการ
          </Box>

          <Box sx={{ 
            width: '380px', 
            textAlign: 'center', 
            mt: 2, 
            fontSize: '10.5pt',
            lineHeight: 1.8 
          }}>
            <Box sx={{ mb: 1 }}>
              ลายมือชื่อ....................................................................
            </Box>
            <Box sx={{ mb: 0.5 }}>
              (....................................................................)
            </Box>
            <Box sx={{ fontWeight: 600 }}>
              ตำแหน่ง ผู้รับรองฝ่ายฝึกประสบการณ์วิชาชีพครู คณะครุศาสตร์ มหาวิทยาลัยราชภัฏกำแพงเพชร
            </Box>
            <Box sx={{ mt: 0.5 }}>
              วันที่......./......./.......
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Image Preview Modal Dialog */}
      <Dialog 
        open={previewModalOpen} 
        onClose={() => setPreviewModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'primary.main', color: 'white', py: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{previewFileName}</Typography>
          <IconButton size="small" onClick={() => setPreviewModalOpen(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2, textAlign: 'center', bgcolor: '#1a1a1a' }}>
          <img 
            src={previewFileUrl} 
            alt={previewFileName} 
            style={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain', borderRadius: 4 }} 
          />
        </DialogContent>
        <DialogActions sx={{ p: 1.5, bgcolor: '#f8fafc' }}>
          <Button 
            variant="outlined" 
            startIcon={<OpenInNewIcon />} 
            onClick={() => window.open(previewFileUrl, '_blank', 'noopener,noreferrer')}
            size="small"
          >
            เปิดภาพเต็มในแท็บใหม่
          </Button>
          <Button onClick={() => setPreviewModalOpen(false)} color="inherit" size="small">
            ปิด
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Portfolio;
